// components/modals/CreateClientModal.jsx
import React from 'react';

const validEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const CreateClientModal = ({
    newClientForm,
    setNewClientForm,
    formatAsPhonePartial,
    onCreate,
    onCancel,
    isCreating = false,
}) => {
    const phoneDigits = ('' + newClientForm.phone).replace(/\D/g, '');
    const isPhoneValid = phoneDigits.length === 10;

    const isValid =
        newClientForm.firstName.trim() &&
        newClientForm.lastName.trim() &&
        isPhoneValid &&
        newClientForm.language &&
        validEmail(newClientForm.email);

    const updateField = (key, value) => {
        setNewClientForm(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-xl text-black">
                <h2 className="text-2xl font-bold mb-4">Create Client User</h2>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">First Name</label>
                        <input
                            type="text"
                            value={newClientForm.firstName}
                            onChange={e => updateField('firstName', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                            placeholder="Enter first name"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">Last Name</label>
                        <input
                            type="text"
                            value={newClientForm.lastName}
                            onChange={e => updateField('lastName', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                            placeholder="Enter last name"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">Phone</label>
                        <input
                            type="text"
                            value={formatAsPhonePartial(newClientForm.phone)}
                            onChange={e => {
                                const onlyDigits = e.target.value.replace(/\D/g, '');
                                if (onlyDigits.length <= 10) updateField('phone', onlyDigits);
                            }}
                            onPaste={e => {
                                e.preventDefault();
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                const onlyDigits = paste.replace(/\D/g, '').slice(0, 10);
                                updateField('phone', onlyDigits);
                            }}
                            className="border border-gray-300 rounded px-2 py-1"
                            placeholder="(123) 456-7890"
                        />
                        {!isPhoneValid && newClientForm.phone && (
                            <div className="text-xs text-red-600 mt-1">Phone must be 10 digits.</div>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">Email (optional)</label>
                        <input
                            type="email"
                            value={newClientForm.email}
                            onChange={e => updateField('email', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                            placeholder="Enter email (optional)"
                        />
                        {!validEmail(newClientForm.email) && (
                            <div className="text-xs text-red-600 mt-1">Enter a valid email address.</div>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">DOB</label>
                        <input
                            type="date"
                            value={newClientForm.dob}
                            onChange={e => updateField('dob', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold">LMP</label>
                        <input
                            type="date"
                            value={newClientForm.menstrual}
                            onChange={e => updateField('menstrual', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                        />
                    </div>

                    <div className="flex flex-col col-span-2">
                        <label className="text-sm font-semibold">Language</label>
                        <select
                            value={newClientForm.language}
                            onChange={e => updateField('language', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                        >
                            <option value="English">English</option>
                            <option value="Spanish">Spanish</option>
                        </select>
                    </div>

                    <div className="flex flex-col col-span-2">
                        <label className="text-sm font-semibold">Admin Notes</label>
                        <textarea
                            value={newClientForm.adminNotes}
                            onChange={e => updateField('adminNotes', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                            rows={3}
                        />
                    </div>

                    <div className="flex flex-col col-span-2">
                        <label className="text-sm font-semibold">Client Note</label>
                        <textarea
                            value={newClientForm.notes}
                            onChange={e => updateField('notes', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1"
                            rows={3}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        disabled={isCreating}
                        className="bg-gray-300 hover:bg-gray-400 text-black font-semibold px-4 py-2 rounded"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={onCreate}
                        disabled={!isValid || isCreating}
                        className={`font-semibold px-4 py-2 rounded transition ${isValid && !isCreating
                                ? 'bg-JMJ hover:bg-JMJGray text-white'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                    >
                        {isCreating ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateClientModal;
